import getConfig from 'next/config'
import React, { createContext, FC, ReactNode, useCallback } from 'react'

import { Breadcrumb } from '@Components/Breadcrumbs/breadcrumb'
import { Field, GlobalNavigationQuery, Maybe, Page, PageItemFragment, SplitTest } from '@Generated/graphql'
import { GlobalAnchorInjections } from '@Globals/Injections/Types'
import { Money } from '@Globals/Money'
import { getStoreURL } from '@Globals/Stores'

export interface AvailableLocale {
  localeCode: string
  title: string
  url: string
}

export interface AlternateLink {
  locale: string
  url: string
}

export interface PageObject {
  breadcrumbs?: Breadcrumb[]
  id: string
  type: string
  url: string
}

export interface CMSURLs {
  aboutUs: Maybe<PageItemFragment>
  articles: Maybe<PageItemFragment>
  cookiePolicy: Maybe<PageItemFragment>
  csr: Maybe<PageItemFragment>
  faq: Maybe<PageItemFragment>
  giftGuide: Maybe<PageItemFragment>
  press: Maybe<PageItemFragment>
  privacyPolicy: Maybe<PageItemFragment>
  returns: Maybe<PageItemFragment>
  terms: Maybe<PageItemFragment>
}

export interface CMSData {
  latestCollections: Page[]
  urls: CMSURLs
}

export interface PaymentMethod {
  icons: PaymentIcon[]
  provider: string
  type: string
}

export interface PaymentIcon {
  logo: { url: string }
  name: string
  url: string
}

type RedirectType = 'category' | 'cms' | 'product'

interface StoreConfigAdwordsCurrencyRate {
  key: string
  value: number
}

export interface StoreConfig {
  adwordsCurrencyRates: Array<StoreConfigAdwordsCurrencyRate>
  adwordsTrackingID: string
  analyticsTrackingID: string
  cheapestShippingPrice?: Money
  countryCode: string
  currencyCode: string
  currencyFraction: number
  email: string
  freeShippingThreshold?: Money
  freeShippingThresholdClub?: Money
  giftcards?: {
    predefinedValues: Array<Money>
    valueMax: Money
    valueMin: Money
  }
  localeCode: string
  merchantAccount: string
  phone: string
  trustpilotBusinessID: string
  trustpilotDomain: string
  urlDomain: string
  urlFull: string
  urlPath: string
  usercentricsEnabled: boolean
  vatNumber: string
}

export interface Store {
  config?: StoreConfig
  id: string
  mainStore: string
  title: string
}

export interface RTEField {
  key: string
  references: []
  value: string
}

export type FacetDescriptions = Record<string, string>

export interface Bar {
  addons?: string[]
  backgroundColor: string
  conditions?: string[]
  fields: Field[]
  id: string
  placement: string[]
  priority: number
  splitTest?: SplitTest
  textColor: string
  theme?: string
  type?: string
  urgencyTimerDateEnd?: Date
  urgencyTimerDateStart?: Date
}

interface Metadata {
  addSurrogateKey: (key: string) => void
  alternateLinks: AlternateLink[]
  availableLocales: AvailableLocale[]
  bars: Bar[]
  cmsData: CMSData
  facetDescriptions: FacetDescriptions
  fonts: string[]
  globalAnchorInjections: GlobalAnchorInjections
  navigation: GlobalNavigationQuery['navigation']
  navigationTop: GlobalNavigationQuery['navigationTop']
  page?: PageObject
  paymentMethods: PaymentMethod[]
  redirectURL: (type: RedirectType, id: string, useStore?: boolean) => string
  store: Store
  storeURL: (path: string, params?: URLSearchParams, useOrigin?: boolean) => string
}

interface MetadataProviderProps {
  addSurrogateKey: (key: string) => void
  alternateLinks: AlternateLink[]
  availableLocales: AvailableLocale[]
  bars: Bar[]
  children: ReactNode
  cmsData: CMSData
  facetDescriptions: FacetDescriptions
  fonts: string[]
  globalAnchorInjections: GlobalAnchorInjections
  navigation: GlobalNavigationQuery['navigation']
  navigationTop: GlobalNavigationQuery['navigationTop']
  overrideStoreURLFull: string | false
  page?: PageObject
  paymentMethods: PaymentMethod[]
  store: Store
}

export const MetadataContext = createContext<Metadata>({} as Metadata)
export const MetadataProvider: FC<MetadataProviderProps> = (
  {
    addSurrogateKey,
    alternateLinks,
    availableLocales,
    bars,
    children,
    cmsData,
    facetDescriptions,
    fonts,
    globalAnchorInjections,
    navigation,
    navigationTop,
    overrideStoreURLFull,
    page,
    paymentMethods,
    store,
  }: MetadataProviderProps,
) => {
  const storeURL = useCallback(
    (path: string, params?: URLSearchParams, useOrigin?: boolean) => getStoreURL(store?.config?.urlFull, overrideStoreURLFull, path, params, useOrigin),
    [overrideStoreURLFull, store?.config?.urlFull],
  )

  const { publicRuntimeConfig } = getConfig()

  const redirectURL = useCallback((
    type: RedirectType,
    id: string,
    useStore: boolean = false,
  ) => {
    const url = new URL(`${publicRuntimeConfig.redirectEndpoint}/redirect/type/${type}/id/${id}`)

    if (useStore) {
      url.searchParams.set('store', store?.id)
    }

    return url.toString()
  }, [publicRuntimeConfig.redirectEndpoint, store?.id])

  return (
    <MetadataContext.Provider
      // TODO: avoid rerendering when no metadata changes
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        addSurrogateKey,
        alternateLinks,
        availableLocales,
        bars,
        cmsData,
        facetDescriptions,
        fonts,
        globalAnchorInjections,
        navigation,
        navigationTop,
        page,
        paymentMethods,
        redirectURL,
        store,
        storeURL,
      }}
    >
      {children}
    </MetadataContext.Provider>
  )
}
